import { selectRisksOf, selectViolationEffects, selectViolationsOf } from '../../demo/selectors'
import type { Ad, Campaign, LibraryCreative, Risk, RiskType, TabKey, Violation } from '../../demo/types'

export type MonitorTabKey = TabKey | 'violation' | 'risk' | 'autopilot'

export const COMPLIANCE_TABS = [
  { key: 'violation', label: '违规' },
  { key: 'risk', label: '风控' },
] as const

export const COMPLIANCE_COPY = {
  violationColumn: '违规广告',
  violationDialogHint: '换素材要过 1.5 秒审核；申诉约 2 秒出结果。两条路都会让违规计数减 1。',
  violationEmpty: '这个广告任务没有违规了',
  riskEmpty: '这个广告任务没有风险了',
  bannedWord: '第一',
  bannedCopy: '记忆棉枕｜颈椎支撑第一的护颈选择，一觉睡到自然醒',
  bannedCopyHint: '文案里还有高危词「第一」',
  autoCloseMs: 800,
} as const

const RISK_LABELS: Record<RiskType, string> = {
  duplicate: '素材重复度过高',
  banned_word: '高危词',
  license: '达人授权即将到期',
}

export function complianceCounts(campaigns: Campaign[], violations: Violation[], risks: Risk[]) {
  return {
    violation: campaigns.filter((campaign) => selectViolationsOf(violations, campaign.id).length > 0).length,
    risk: campaigns.filter((campaign) => selectRisksOf(risks, campaign.id).length > 0).length,
  }
}

export function filterComplianceCampaigns(
  campaigns: Campaign[],
  tab: MonitorTabKey,
  violations: Violation[],
  risks: Risk[],
): Campaign[] {
  if (tab === 'violation') return campaigns.filter((campaign) => selectViolationsOf(violations, campaign.id).length > 0)
  if (tab === 'risk') return campaigns.filter((campaign) => selectRisksOf(risks, campaign.id).length > 0)
  return campaigns
}

export function violationLabel(violations: Violation[] | number): string {
  if (typeof violations === 'number') return `${violations} 条广告因素材违规被拒登`
  const campaignId = violations[0]?.campaignId
  if (!campaignId) return '0 条广告因素材违规被处置'
  const effects = selectViolationEffects(violations, campaignId)
  if (effects.length === 1) return `${violations.length} 条广告因素材违规${effects[0]?.effect ?? '被处置'}`
  const detail = effects.map((item) => `${item.effect} ${item.count}`).join(' · ')
  return `${violations.length} 条广告因素材违规被处置（${detail}）`
}

export function riskTitle(count: number): string {
  return `${count} 项合规风险待处理`
}

export function riskSummary(risks: Risk[]): string {
  return risks.map((risk) => RISK_LABELS[risk.type]).join(' · ')
}

export function violationDialogTitle(count: number): string {
  return `处理违规广告（${count} 条）`
}

export function riskDialogTitle(count: number): string {
  return `合规风险（${count} 项）`
}

export function duplicateTargetAd(ads: Ad[], campaignId: string): Ad | undefined {
  const uses = ads.reduce<Map<string, number>>((counts, ad) => {
    counts.set(ad.creativeId, (counts.get(ad.creativeId) ?? 0) + 1)
    return counts
  }, new Map())
  return ads
    .filter((ad) => ad.campaignId === campaignId)
    .sort((left, right) => (uses.get(right.creativeId) ?? 0) - (uses.get(left.creativeId) ?? 0))[0]
}

export function licenseTargetCreative(
  ads: Ad[],
  creatives: LibraryCreative[],
  campaignId: string,
  riskText: string,
): LibraryCreative | undefined {
  const pool = ads
    .filter((ad) => ad.campaignId === campaignId)
    .map((ad) => creatives.find((creative) => creative.id === ad.creativeId))
    .filter((creative): creative is LibraryCreative => creative?.source === 'kol')
  return pool.find((creative) => creative.kol && riskText.includes(creative.kol)) ?? pool[0]
}
